import styles from "./EstimateLayoutStep7.module.scss";
import classNames from "classnames/bind";
import TitleEditor from "~/components/common/form/titleEdit/TitleEditor";
import FormInpuEditor from "~/components/common/form/formInpuEditor/FormInpuEditor";
import FormToggle from "~/components/common/form/formToggle/FormToggle";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import type { EstimateLayoutStep7FormData as Step7FormData } from "~/utils/interface/estimateSetting";
import { IEstimateTemplateIntroduction } from "~/utils/interface/estimateSetting";
import { useState, useRef, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { handleGetEstimateTemplateIntroduction } from "~/thunks/estimate-setting/estimateSettingThunk";

const cx = classNames.bind(styles);

interface EstimateLayoutStep7Props {
    onStep7DataChange?: (data: Step7FormData) => void;
    initialData?: Step7FormData | null;
    onPreview?: () => void;
}

const EstimateLayoutStep7 = ({ onStep7DataChange, initialData, onPreview }: EstimateLayoutStep7Props) => {
    const dispatch = useAppDispatch();
    const { estimateTemplateIntroduction } = useAppSelector((state) => state.estimateSetting);

    const DEFAULT_TITLE_STEP = "Step 7: CBR Insurance";
    const [formData, setFormData] = useState<Step7FormData | undefined>(() => ({
        ...initialData,
        titleStep7: initialData?.titleStep7 ?? DEFAULT_TITLE_STEP,
    }));

    const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const templateDropdownRef = useRef<HTMLDivElement>(null);
    const formDataRef = useRef(formData);

    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (templateDropdownRef.current && !templateDropdownRef.current.contains(event.target as Node)) {
                setTemplateDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleUpdateData = (update: Partial<Step7FormData>) => {
        const data: Step7FormData = { ...(formDataRef.current ?? {}), ...update } as Step7FormData;
        formDataRef.current = data;
        setFormData(data);
        onStep7DataChange?.(data);
    };

    const handleUseTemplateClick = () => {
        const nextOpen = !templateDropdownOpen;
        setTemplateDropdownOpen(nextOpen);
        if (nextOpen) {
            setTemplatesLoading(true);
            dispatch(handleGetEstimateTemplateIntroduction()).finally(() => {
                setTemplatesLoading(false);
            });
        }
    };

    const handleSelectTemplate = (template: IEstimateTemplateIntroduction) => {
        handleUpdateData({
            titleStep7: template.estimateTemplate.templateName ?? "",
            insuranceDetails: template.estimateTemplate.templateDescription ?? "",
        });
        setTemplateDropdownOpen(false);
    };

    return (
        <div className={cx('estimate-layout-step-7-container')}>
            <div className={cx('header')}>
                <TitleEditor
                    value={formData?.titleStep7}
                    name={""}
                    placeholder={""}
                    inputkey={""}
                    isViewMode={false}
                    onChange={(value) => {
                        handleUpdateData({ titleStep7: value });
                    }}
                    styleView={{
                        fontSize: "20px",
                        fontWeight: 700,
                    }}
                />

                <div className={cx('button-container')} ref={templateDropdownRef}>
                    <div className={cx("template-dropdown-wrapper")}>
                        <button
                            type="button"
                            className={cx("left-content-body-menu-item")}
                            onClick={handleUseTemplateClick}
                        >
                            Use Template
                        </button>
                        {templateDropdownOpen && (
                            <div className={cx("template-dropdown")}>
                                {templatesLoading ? (
                                    <div className={cx("template-dropdown-loading")}>
                                        Loading...
                                    </div>
                                ) : (
                                    <ul className={cx("template-dropdown-list")}>
                                        {estimateTemplateIntroduction.length === 0 ? (
                                            <li className={cx("template-dropdown-empty")}>No templates</li>
                                        ) : (
                                            estimateTemplateIntroduction.map((template) => (
                                                <li key={template.id}>
                                                    <button
                                                        type="button"
                                                        className={cx("template-dropdown-item")}
                                                        onClick={() => handleSelectTemplate(template)}
                                                    >
                                                        {template.estimateTemplate.templateName || "Unnamed template"}
                                                    </button>
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>

                    <PrimaryButton
                        title="View Page"
                        onClick={() => onPreview?.()}
                        className={cx('btn-preview')}
                    />
                </div>
            </div>
            <div className={cx('content')}>
                <FormInpuEditor
                    name={"insuranceDetails"}
                    text={""}
                    placeholder={""}
                    inputkey={"insuranceDetails"}
                    value={formData?.insuranceDetails}
                    isViewMode={false}
                    errorMessage={""}
                    height="200px"
                    onChange={(value) => {
                        handleUpdateData({ insuranceDetails: value });
                    }}
                />
            </div>

            <div className={cx('acknowledge-row')}>
                <h3 className={cx('cbr-help')}>Require customers to acknowledge this page</h3>
                <FormToggle
                    inputkey={"isAcknowledged"}
                    value={formData?.isAcknowledged ?? false}
                    isViewMode={false}
                    onChange={(e) => {
                        handleUpdateData({ isAcknowledged: e.newValue });
                    }}
                    text={""}
                />
            </div>
        </div>
    );
};

export default EstimateLayoutStep7;
